import PlusCheckout from 'components/PlusCheckout';
import Modal from 'components/Modal';
import { ComponentProps, useEffect, useReducer, useState } from 'react';
import useUser from 'hooks/useUser';
import { getAvailablePlans } from 'api/Purchases';
import useIpCountryCode from 'hooks/useIpCountryCode';
import Coupon from 'types/Coupon';
import { UserId } from '@alltrails/shared/types/User';
import Plan from 'types/Plan';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import logProCheckoutModalViewed from '@alltrails/analytics/events/logProCheckoutModalViewed';
import * as styles from './CheckoutModal.module.scss';

type Props = {
  coupon?: Coupon;
  isOpen: boolean;
  onRequestClose: ComponentProps<typeof Modal>['onRequestClose'];
  plan?: Plan;
  returnTo?: string | (() => string);
  trigger: CarouselDisplayTrigger;
  userId?: UserId;
};

// Desktop: https://www.figma.com/file/UsRZiiso2irMJYKykeo6xK/On-page-web-checkout?node-id=568-140350&t=3ncSlmftPdlHgdzZ-4
// Mobile: https://www.figma.com/file/UsRZiiso2irMJYKykeo6xK/On-page-web-checkout?node-id=568-140712&t=3ncSlmftPdlHgdzZ-4
export default function CheckoutModal({ coupon, isOpen, onRequestClose, plan, returnTo, trigger, userId }: Props) {
  const countryCode = useIpCountryCode();
  const user = useUser();
  const [availablePlans, setAvailablePlans] = useState<Awaited<ReturnType<typeof getAvailablePlans>>>();
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  useEffect(() => {
    function onLoad() {
      forceUpdate();
    }

    let script: HTMLScriptElement;

    function loadRecurly() {
      const src = 'https://js.recurly.com/v4/recurly.js';

      const existing = document.querySelector(`script[src="${src}"]`);

      if (!existing) {
        script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.addEventListener('load', onLoad);
        document.body.appendChild(script);
      }
    }

    if (isOpen && typeof recurly === 'undefined') {
      loadRecurly();
    }

    return () => {
      if (script) {
        script.removeEventListener('load', onLoad);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    async function getPlans() {
      const response = await getAvailablePlans(countryCode);
      setAvailablePlans(response);
    }

    if (isOpen) {
      getPlans();
    }
  }, [countryCode, isOpen]);

  useEffect(() => {
    if (isOpen) {
      logProCheckoutModalViewed({ trigger });
    }
  }, [isOpen, trigger]);

  if (typeof recurly === 'undefined' || !availablePlans) {
    return null;
  }

  // Add querystring param for BugSnag support to determine why this uncaught exception is not being reported to BugSnag. They're going to debug into
  // it on production.
  const params = new URLSearchParams(__AT_DATA__.search);
  if (params.has('bugsnag')) {
    const bugsnag = undefined || user.id;
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} size="auto">
      <PlusCheckout
        annualPlan={availablePlans.nonTrial}
        className={styles.checkout}
        coupon={coupon}
        currencyCode={availablePlans.currencyCode}
        initialPlan={plan || (!coupon && user.isPromoEligible ? availablePlans.trial : availablePlans.nonTrial)}
        returnTo={returnTo}
        userId={userId || user.id}
      />
    </Modal>
  );
}
